import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';

import Date from '../../../common/date';
import Money from '../../../common/money';
import InvoiceActions from './InvoiceActions';
import Pagination from '../../../common/Pagination';
import PaymentOptionsModal from '../../../main/customers/customerForm/containers/PaymentOptionsModal';
import InvoiceChangeDueDateModal from '../InvoiceChangeDueDate/InvoiceChangeDueDateModal';
import InvoiceAction from './InvoiceAction';
import { INVOICES_LIST_PAGE_SIZE } from './InvoicesListContainer';
import SetupPaymentInfoModal from '../../../common/SetupPaymentInfoModal';

const invoiceStatuses = {
  upcoming: {
    label: 'Upcoming',
    value: 'upcoming',
  },
  paid: {
    label: 'Paid',
    value: 'paid',
  },
  markedAsPaid: {
    label: 'Marked as Paid',
    value: 'marked_as_paid',
  },
  failed: {
    label: 'Failed',
    value: 'failed',
  },
  uncollectible: {
    label: 'Uncollectible',
    value: 'uncollectible',
  },
  refunded: {
    label: 'Refunded',
    value: 'refunded',
  },
};

const InvoicesList = props => (
  <div className="u-width-100">
    <div
      className={cx(
        'o-flexbox u-mb+ u-mt',
        props.title ? 'o-flexbox--justify-space-between' : 'o-flexbox--justify-end',
      )}
    >
      <div className="c-heading" display-if={props.title}>{props.title}</div>
      <select
        className="c-select u-max-width-300"
        onChange={props.handleStatusChange}
        value={props.activeStatus}
      >
        <option value="all">All Statuses</option>
        {
          Object.values(invoiceStatuses).map(item => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))
        }
      </select>
    </div>
    <ReactTable
      className="c-table-new c-no-hover c-table-paginated u-mb c-invoices-table"
      data={props.activePageItems}
      minRows={0}
      defaultPageSize={INVOICES_LIST_PAGE_SIZE}
      showPagination={false}
      resizable={false}
      getTrProps={(state, row) => {
        const isDisabled = props.pendingInvoices[row.original.id];
        return {
          className: cx({
            'u-opacity': isDisabled,
            'no-pointer-events': isDisabled,
          }),
        };
      }}
      columns={[
        {
          Header: 'Amount',
          accessor: 'price',
          id: 'price',
          Cell: row => (row.value ? <Money>{row.value}</Money> : 'N/A'),
        },
        {
          Header: 'Invoice Number',
          accessor: 'invoiceNumber',
          id: 'invoiceNumber',
        },
        {
          Header: 'Shop Name',
          accessor: invoice => props.shopNamesById[invoice.ownerId] || '',
          id: 'shopName',
          show: props.showShopColumn,
        },
        {
          Header: 'Member Name',
          accessor: 'subscriberName',
          id: 'subscriberName',
          show: props.showMemberName,
        },
        {
          Header: 'Method',
          accessor: invoice => `${invoice.cardBrand} ${invoice.cardLast4}`,
          id: 'method',
        },
        {
          Header: 'Due Date',
          accessor: 'dueDate',
          id: 'dueDate',
          Cell: row => (row.value ? <Date>{row.value}</Date> : 'N/A'),
        },
        {
          Header: 'Created',
          accessor: 'createdAt',
          id: 'createdAt',
          Cell: row => (row.value ? <Date>{row.value}</Date> : 'N/A'),
        },
        {
          Header: 'Status',
          accessor: 'status',
          id: 'status',
          Cell: row => (
            <div className={`c-memberships-status c-memberships-status__${row.value}`}>
              {row.value}
            </div>
          ),
        },
        {
          Header: props.showActionsCompact ? '' : 'Actions',
          className: props.showActionsCompact ? 'o-flexbox--justify-end' : '',
          id: 'actions',
          Cell: row => (
            <InvoiceActions
              invoice={row.original}
              performAction={props.performAction}
              setAction={props.setAction}
              isPaymentInfoSetup={props.isPaymentInfoSetup}
            />
          ),
        },
      ]}
    />
    <Pagination
      display-if={props.activePageItems.length}
      onPageChange={props.openPage}
      pageCount={props.totalPages}
      currentPage={props.activePage}
    />
    <PaymentOptionsModal
      display-if={props.selectedAction === InvoiceAction.modifyPaymentMethod}
      customerId={props.activeInvoice.subscriberId}
      canSelect
      onCardSelect={props.modifyPaymentMethod}
      selectedCardId={props.activeInvoice.paymentMethodSourceId}
      close={props.clearSelectedAction}
    />
    <InvoiceChangeDueDateModal
      display-if={props.selectedAction === InvoiceAction.changeDueDate}
      dueDate={props.activeInvoice.dueDate}
      onSubmit={props.changeDueDate}
      close={props.clearSelectedAction}
    />
    <SetupPaymentInfoModal
      isOpen={props.selectedAction === InvoiceAction.setupPaymentInfo}
      onClose={props.clearSelectedAction}
    />
  </div>
);

InvoicesList.propTypes = {
  activePageItems: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  activePage: PropTypes.number.isRequired,
  openPage: PropTypes.func.isRequired,
  handleStatusChange: PropTypes.func.isRequired,
  activeStatus: PropTypes.string.isRequired,
  performAction: PropTypes.func.isRequired,
  pendingInvoices: PropTypes.object.isRequired,
  showMemberName: PropTypes.bool.isRequired,
  showActionsCompact: PropTypes.bool,
  modifyPaymentMethod: PropTypes.func.isRequired,
  changeDueDate: PropTypes.func.isRequired,
  activeInvoice: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]).isRequired,
  clearSelectedAction: PropTypes.func.isRequired,
  selectedAction: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]).isRequired,
  setAction: PropTypes.func.isRequired,
  showShopColumn: PropTypes.bool.isRequired,
  shopNamesById: PropTypes.object.isRequired,
  title: PropTypes.node,
  isPaymentInfoSetup: PropTypes.bool.isRequired,
};

InvoicesList.defaultProps = {
  title: null,
  showMemberName: true,
  showActionsCompact: false,
};

export default InvoicesList;
